import React, { useState, useEffect, useCallback } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { initializeApp } from "firebase/app";
import { getAuth, signInAnonymously, signInWithCustomToken } from "firebase/auth";
import { getFirestore, doc, setDoc, getDoc, deleteDoc, onSnapshot, collection } from "firebase/firestore";

import MessageBox from './components/MessageBox';
import ConfirmDeleteModal from './components/ConfirmDeleteModal';
import InvoiceHeader from './components/InvoiceHeader';
import BillToSection from './components/BillToSection';
import InvoiceTable from './components/InvoiceTable';
import PaymentSummary from './components/PaymentSummary';
import PaymentTerms from './components/PaymentTerms';
import Controls from './components/Controls';
import Login from './components/Login';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);
const appId = process.env.REACT_APP_FIREBASE_APP_ID || 'default-app-id';
const initialAuthToken = process.env.REACT_APP_INITIAL_AUTH_TOKEN || null;

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState('[Invoice Number]');
  const [invoiceDate, setInvoiceDate] = useState('2025-08-07');
  const [dueDate, setDueDate] = useState('[Due Date]');
  const [client, setClient] = useState({
    name: "[Client's Name]",
    company: "[Client's Company]",
    address: "[Client's Address]",
    cityStateZip: "[Client's City, State, Zip]",
  });
  const [items, setItems] = useState([
    { description: "Software Development: New Feature for user's needs. This is a longer description to demonstrate how the column will now expand.", quantity: 10, unitPrice: 125.00 },
    { description: 'Electronics Repair: Motherboard Replacement', quantity: 1, unitPrice: 250.00 },
  ]);
  const [subtotal, setSubtotal] = useState(0);
  const [tax, setTax] = useState(0);
  const [totalDue, setTotalDue] = useState(0);
  const [message, setMessage] = useState(null);
  const [isErrorMessage, setIsErrorMessage] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [invoiceToDeleteId, setInvoiceToDeleteId] = useState(null);
  const [loadInvoiceId, setLoadInvoiceId] = useState('');
  const [savedInvoices, setSavedInvoices] = useState([]);

  const handleLogin = (status) => {
    setIsLoggedIn(status);
  };

  const showMessageBox = useCallback((msg, isError = false) => {
    setMessage(msg);
    setIsErrorMessage(isError);
  }, []);

  const authenticate = useCallback(async () => {
    try {
      if (auth) {
        if (initialAuthToken) {
          await signInWithCustomToken(auth, initialAuthToken);
        } else {
          await signInAnonymously(auth);
        }
        console.log('Firebase authentication successful.');
      }
    } catch (error) {
      console.error("Firebase authentication error:", error);
    }
  }, []);

  const calculateDueDate = useCallback(() => {
    if (invoiceDate) {
      try {
        const date = new Date(invoiceDate);
        date.setDate(date.getDate() + 6);
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        setDueDate(`${year}-${month}-${day}`);
      } catch (e) {
        console.error("Invalid date format", e);
        setDueDate('[Due Date]');
      }
    }
  }, [invoiceDate]);

  const updatePaymentSummary = useCallback(() => {
    let newSubtotal = 0;
    items.forEach(item => {
      newSubtotal += item.quantity * item.unitPrice;
    });

    const taxRate = 0.10; // 10% tax rate
    const newTax = newSubtotal * taxRate;
    const newTotalDue = newSubtotal + newTax;

    setSubtotal(newSubtotal);
    setTax(newTax);
    setTotalDue(newTotalDue);
  }, [items]);

  const addRow = useCallback((description = "New Item", quantity = 1, unitPrice = 0.00) => {
    setItems(prevItems => [...prevItems, { description, quantity, unitPrice }]);
  }, []);

  const handleRemoveItem = useCallback((indexToRemove) => {
    setItems(prevItems => prevItems.filter((_, index) => index !== indexToRemove));
  }, []);

  const getInvoiceData = useCallback(() => {
    return {
      invoiceNumber,
      date: invoiceDate,
      dueDate,
      client,
      items: items.map(item => ({
        description: item.description,
        quantity: parseFloat(item.quantity) || 0,
        unitPrice: parseFloat(item.unitPrice) || 0,
      })),
    };
  }, [invoiceNumber, invoiceDate, dueDate, client, items]);

  const populateInvoice = useCallback((data) => {
    setInvoiceNumber(data.invoiceNumber);
    setInvoiceDate(data.date);
    setDueDate(data.dueDate);
    setClient(data.client);
    setItems(data.items);
  }, []);

  const loadInvoice = useCallback(async (id) => {
    if (!db) {
      showMessageBox("Cannot load. Please check console for details.", true);
      return;
    }

    const docRef = doc(db, `/artifacts/${appId}/public/data/invoices/${id}`);

    try {
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        console.log("Document data:", docSnap.data());
        populateInvoice(docSnap.data());
        showMessageBox(`Invoice ${id} loaded successfully!`);
      } else {
        console.log("No such document!");
        showMessageBox("No invoice found with that ID.", true);
      }
    } catch (e) {
      console.error("Error getting document: ", e);
      showMessageBox("An error occurred while loading the invoice. Please check the console.", true);
    }
  }, [populateInvoice, showMessageBox]);

  const saveInvoice = useCallback(async () => {
    if (!db) {
      console.error("Firestore not initialized.");
      showMessageBox("Cannot save. Please check console for details.", true);
      return;
    }
    const id = invoiceNumber.replace(/[^a-zA-Z0-9]/g, '');
    if (!id) {
      showMessageBox("Please enter an Invoice Number to save.", true);
      return;
    }
    const invoiceData = getInvoiceData();
    const docRef = doc(db, `/artifacts/${appId}/public/data/invoices/${id}`);

    try {
      await setDoc(docRef, invoiceData);
      showMessageBox(`Invoice ${id} saved successfully!`);
      console.log("Document successfully written with ID: ", id);
    } catch (e) {
      console.error("Error adding document: ", e);
      showMessageBox("An error occurred while saving the invoice. Please check the console.", true);
    }
  }, [invoiceNumber, getInvoiceData, showMessageBox]);

  const deleteInvoice = useCallback(async (id) => {
    if (!db) {
      showMessageBox("Cannot delete. Please check console for details.", true);
      return;
    }

    const docRef = doc(db, `/artifacts/${appId}/public/data/invoices/${id}`);
    try {
      await deleteDoc(docRef);
      showMessageBox(`Invoice ${id} deleted successfully!`);
      console.log("Document successfully deleted with ID: ", id);
    } catch (e) {
      console.error("Error deleting document: ", e);
      showMessageBox("An error occurred while deleting the invoice. Please check the console.", true);
    }
  }, [showMessageBox]);

  const preparePrintableInvoice = useCallback((invoiceContainer) => {
    const printContainer = invoiceContainer.cloneNode(true);

    // Remove 'Remove' buttons from the printable content
    const removeButtons = printContainer.querySelectorAll('.remove-button');
    removeButtons.forEach(button => button.remove());

    // Remove the 'Action' column header
    const actionHeader = printContainer.querySelector('.action-column');
    if (actionHeader) {
      actionHeader.remove();
    }

    // Remove the 'Action' data cells
    const actionCells = printContainer.querySelectorAll('td.action-column');
    actionCells.forEach(cell => cell.remove());

    const inputs = printContainer.querySelectorAll('input');
    inputs.forEach(input => {
      const span = document.createElement('span');
      span.textContent = input.value;
      if (input.classList.contains('text-right')) {
        span.classList.add('text-right');
      }
      if (input.classList.contains('font-normal')) {
        span.classList.add('font-normal');
      }
      span.classList.add('text-gray-800');
      input.parentNode.replaceChild(span, input);
    });

    const billToSection = printContainer.querySelector('#bill-to-section');
    if (billToSection) {
      const newBillToContent = document.createElement('div');
      newBillToContent.className = 'text-sm text-gray-600 space-y-1';

      newBillToContent.innerHTML = `
        <p class="block my-0.5 pb-0.5">${client.name}</p>
        <p class="block my-0.5 pb-0.5">${client.company}</p>
        <p class="block my-0.5 pb-0.5">${client.address}</p>
        <p class="block my-0.5 pb-0.5">${client.cityStateZip}</p>
      `;

      billToSection.querySelector('.text-sm').replaceWith(newBillToContent);
    }

    return printContainer;
  }, [client]);

  const exportPdf = useCallback(() => {
    const invoiceContent = document.getElementById('invoice-container');
    if (!invoiceContent) {
      showMessageBox("Invoice content not found for PDF export.", true);
      return;
    }

    const printableContent = preparePrintableInvoice(invoiceContent);

    printableContent.style.position = 'absolute';
    printableContent.style.left = '-9999px';
    document.body.appendChild(printableContent);

    html2canvas(printableContent, { scale: 2, useCORS: true }).then(canvas => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = canvas.height * imgWidth / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const filename = `invoice-${invoiceNumber}.pdf`;
      pdf.save(filename);

      document.body.removeChild(printableContent);
    }).catch(error => {
      console.error("Error exporting PDF:", error);
      showMessageBox("An error occurred during PDF export.", true);
    });
  }, [invoiceNumber, preparePrintableInvoice, showMessageBox]);

  useEffect(() => {
    authenticate();
    calculateDueDate();
  }, [authenticate, calculateDueDate]);

  useEffect(() => {
    updatePaymentSummary();
  }, [items, updatePaymentSummary]);

  useEffect(() => {
    if (!db) {
      console.error("Firestore not initialized for listener.");
      return;
    }
    const invoicesCollectionRef = collection(db, `/artifacts/${appId}/public/data/invoices`);
    const unsubscribe = onSnapshot(invoicesCollectionRef, (querySnapshot) => {
      const invoices = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        invoices.push({
          id: doc.id,
          invoiceNumber: data.invoiceNumber || doc.id,
          date: data.date || 'N/A',
          company: data.client?.company || 'No Company'
        });
      });
      setSavedInvoices(invoices);
    });
    return () => unsubscribe();
  }, []);

  const handleItemChange = (newItems) => {
    setItems(newItems);
 };

  const handleConfirmDelete = (id) => {
    deleteInvoice(id);
    setShowDeleteModal(false);
    setInvoiceToDeleteId(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setInvoiceToDeleteId(null);
  };

  const showConfirmDeleteModal = (id) => {
    setInvoiceToDeleteId(id);
    setShowDeleteModal(true);
  };

  if (!isLoggedIn) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="bg-gray-100 flex flex-col items-center py-12">
      <MessageBox message={message} isError={isErrorMessage} onClose={() => setMessage(null)} />
      <ConfirmDeleteModal
        show={showDeleteModal}
        invoiceId={invoiceToDeleteId}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <div className="container bg-white shadow-lg rounded-xl p-8 md:p-12" id="invoice-container">
        <InvoiceHeader
          invoiceNumber={invoiceNumber}
          invoiceDate={invoiceDate}
          dueDate={dueDate}
          onInvoiceNumberChange={setInvoiceNumber}
          onInvoiceDateChange={setInvoiceDate}
          onDueDateChange={setDueDate}
        />
        <BillToSection client={client} onClientChange={setClient} />
        <InvoiceTable items={items} onItemsChange={handleItemChange} updatePaymentSummary={updatePaymentSummary} onRemoveItem={handleRemoveItem} />
        <PaymentSummary subtotal={subtotal} tax={tax} totalDue={totalDue} />
        <PaymentTerms />
      </div>

      <Controls
        onAddItem={addRow}
        onExportPdf={exportPdf}
        onSaveInvoice={saveInvoice}
        onLoadInvoice={() => loadInvoice(loadInvoiceId)}
        onLoadInvoiceIdChange={setLoadInvoiceId}
        loadInvoiceId={loadInvoiceId}
        savedInvoices={savedInvoices}
        onSelectSavedInvoice={loadInvoice}
        onDeleteSavedInvoice={showConfirmDeleteModal}
      />
    </div>
  );
}

export default App;